import { Series, ValueAxis } from "@progress/kendo-angular-charts";

export interface WidgetApiCallListener {
    currentApiIndex: number;
    finalApiIndex: number;
    receviedFinalApiIndex: boolean;
    receivedPreviousVersion: boolean;
}

export interface SiteHealthRateWidget{
    rate: number;
    siteHealthRateElement: SiteHealthRateElement;
}
export interface SiteHealthRateElement{
    crPending: number;
    dueNewFrontTyre: number;
    dueRimNDT: number;
    dueScrapRim: number;
    fsError: number;
    inspectTyre: number;
    msError: number;
    msPending: number;
    stError: number;
    viNotAct: number;
}

export interface ErrorsInWidgetElement {
    componentReceive: number;
    fieldSurveyError: number;
    incompletedEndOfMonth: number;
    maintenanceSessionError: number;
    stocktakesInError: number;
    purchaseOrdersInError: number;
    receivedResult: boolean;
}

export interface CompletedChangeWidgetElement {
    scheduledMaintenanceSession: number;
    changedCompletedMaintenanceSession: number;
}

export interface TyreInInspectionElement {
    inspectionCount: number;
    scrappedMonthCount: number;
}

export interface LiveForecastSelectedResult {
    overDueCount: number;
    dueIn2WeeksCount: number;
    dueIn2To4WeeksCount: number;
    dueIn4To8WeeksCount: number;
    dueIn8To12WeeksCount: number;
}

export interface PressureKpiResult{
    hasTargetKpi: boolean;
    targetKpiValue: number;
    currentKpiValue: number;
}

export interface WidgetScrapLifeTrendChartResult {
    index: number;
    label: string;
    previousYearAvgHours: number;
    targetHours: number;
    new: WidgetScrapLifeTrendChartItem;
    majorRepair: WidgetScrapLifeTrendChartItem;
    transfer: WidgetScrapLifeTrendChartItem;
    retread: WidgetScrapLifeTrendChartItem;
    cosmeticRepair: WidgetScrapLifeTrendChartItem;
    secondHand: WidgetScrapLifeTrendChartItem;
    total: WidgetScrapLifeTrendChartItem;
}

export interface WidgetScrapLifeTrendChartItem {
    hasValue: boolean;
    qty: number;
    avgHours: number;
}

export interface WidgetScrapLifeTrendChartVisibleControl {
    new: boolean;
    majorRepair: boolean;
    transfer: boolean;
    cosmeticRepair: boolean;
    retread: boolean;
    secondHand: boolean;
    targetHour: boolean;
    previousYearAvgHours: boolean;
}

export interface ScrapLifeTrendOverviewResponse {
    startDate: Date;
    endDate: Date;
    displayPeriodMonth: number;
    isAuth: boolean;
    chart: Array<WidgetScrapLifeTrendChartResult>;
    include: WidgetScrapLifeTrendChartVisibleControl;
    chartMax: ScrapLifeTrendChartMax;
    chartColor: ScrapLifeTrendChartColorType;
}

export interface ScrapLifeTrendChartMax {
    qty: number;
    hours: number;
};

export interface ScrapLifeTrendChartColorType {
    targetHours: string;
    new: string;
    majorRepair: string;
    transfer: string;
    cosmeticRepair: string;
    retread: string;
    secondHand: string;
    previousYearAvgHours: string;
}

export interface ScrapLifeTrendChartComponent {
    avgTotalHours: number,
    scrappedQty: number,
    label: string;
}


export interface ScrapLifeTrendWidget {
    siteDashboardWidgetId: string;
    isAuth: boolean;
    apiResult: ScrapLifeTrendOverviewResponse;
    receivedResult: boolean;
}

export interface PressureKPIWidget {
    siteDashboardWidgetId: string;
    hasTargetKpi: boolean;
    receivedResult: boolean;
    targetKpiValue: number;
    currentKpiValue: number;
    maxKpiValue: number;
    minorUnit: number;
    majorUnit: number;
}

export interface PressureCheckWidget {
    siteDashboardWidgetId: string;
    reportResult: Array<PressureCheckVehiceWidgetResult>;
    hasReceivedResult: boolean;
}

export interface PressureCheckVehiceWidgetResult {
    serialNumber: string;
    applicationName: string;
    modelName: string;
    manufacturerName: string;
    pressureCheckCount: number;
    fieldSuryveyCount: number;
    pendingMaintenanceSessionCount: number;
}

//Live Forecast (Global)
export interface LiveForecastChangeType {
    fitmentType: string;
    removalType: string;
}

export interface LiveForecastDialogItem {
    action: boolean;
    siteDashboardWidgetId: string;
}

export interface LiveForecastGroupValueValidation {
    hasOverDue: boolean;
    hasNext2Weeks: boolean;
    hasNext2To4Weeks: boolean;
    hasNext4To8Weeks: boolean;
    hasNext8To12Weeks: boolean;
}

export interface LiveForecastTaskType {
    code: string,
    name?: string,
    isOverDue: boolean,
    isNext2Weeks: boolean,
    isNext2To4Weeks: boolean,
    isNext4To8Weeks: boolean,
    isNext8To12Weeks: boolean,
    changeType: LiveForecastChangeType
}

export interface LiveForecastSelectionFilterOption {
    overDue: Array<string>;
    next2Weeks: Array<string>;
    next2To4Weeks: Array<string>;
    next4To8Weeks: Array<string>;
    next8To12Weeks: Array<string>;
}


//Live Forecast - Tyre
export interface LiveForecastTyreWidget {
    siteDashboardWidgetId: string;
    result: LiveForecastTyreResponse;
}

export interface LiveForecastTyreResponse {
    hasLiveForecast: boolean;
    allTyres: LiveForecastTyreGroup;
    newChangeTyres: LiveForecastTyreGroup;
    endLifeTyres: LiveForecastTyreGroup;
}

export interface LiveForecastTyreGroup{
    overDue: Array<LiveForecastTyreElement>;
    next2Weeks: Array<LiveForecastTyreElement>;
    next2To4Weeks: Array<LiveForecastTyreElement>;
    next4To8Weeks: Array<LiveForecastTyreElement>;
    next8To12Weeks: Array<LiveForecastTyreElement>;
    filterOption: LiveForecastTyreFilterOption;
}

export interface LiveForecastTyreElement {
    dueDate: Date,
    lastFitmentDate: Date,
    dueDateField: string,
    lastDateFitmentDateField: string,
    serialNumber: string,
    size: string,
    specification: string,
    currentTyrePosition: string,
    currentTyreLocation: string,
    currentTyreHours: string,
    remainingTreadDepth: string,
    originalTreadDepth: string,
    remainingTreadPercent: string,
    task: LiveForecastTaskType,
    fittedVehicle: LiveForecastFittedTyreVehicleElement
}

export interface LiveForecastFittedTyreVehicleElement {
    serialNumber: string,
    specification: string,
}

export interface LiveForecastTyreFilterOption {
    vehicleSerialNumberList: LiveForecastSelectionFilterOption;
    vehicleSpecifiationList: LiveForecastSelectionFilterOption;
    tyreSizeList: LiveForecastSelectionFilterOption;
    tyreSpecificationList: LiveForecastSelectionFilterOption;
    filter: LiveForecastTyreSelectedFilter;
    validate: LiveForecastGroupValueValidation;
}

export interface LiveForecastSelectedGroupCount {
    overDue: number;
    next2Weeks: number;
    next2To4Weeks: number;
    next4To8Weeks: number;
    next8To12Weeks: number;
}

export interface LiveForecastTyreSelectedFilter {
    vehicleSerialNumber: Array<string>;
    vehicleSpecification: Array<string>;
    tyreSize: Array<string>;
    tyreSpecification: Array<string>;
}

export interface LiveForecastTyreSelectedFilterItem {
    tyreSizeList: Array<string>;
    tyreSpecificationList: Array<string>;
    vehicleSerialNumberList: Array<string>;
    vehicleSpecificationList: Array<string>;
    filter: LiveForecastTyreSelectedFilter;
}

export interface LiveForecastTyreApplyFilter {
    tyreSizeList: Array<string>;
    tyreSpecificationList: Array<string>;
    vehicleSerialNumberList: Array<string>;
    vehicleSpecificationList: Array<string>;
}


//Live Forecast - Rim
export interface LiveForecastRimWidget {
    siteDashboardWidgetId: string;
    result: LiveForecastRimResponse;
}

export interface LiveForecastRimResponse {
    hasLiveForecast: boolean;
    allRims: LiveForecastRimGroup;
    ndtRim: LiveForecastRimGroup;
    scrapRim: LiveForecastRimGroup;
}

export interface LiveForecastRimGroup {
    overDue: Array<LiveForecastRimElement>;
    next2Weeks: Array<LiveForecastRimElement>;
    next2To4Weeks: Array<LiveForecastRimElement>;
    next4To8Weeks: Array<LiveForecastRimElement>;
    next8To12Weeks: Array<LiveForecastRimElement>;
    filterOption: LiveForecastRimFilterOption;
}

export interface LiveForecastRimElement {
    task: LiveForecastTaskType;
    fittedVehicle: LiveforecastRimFittedVehicleElement;
    dueDate: Date;
    rimSerialNumber: string;
    rimSize: string;
    rimSpecification: string;
    currentPosition: number;
    currentTotalHours: number;
    currentHourSinceLastNdt: number;
}

export interface LiveforecastRimFittedVehicleElement {
    vehicleSerialNumber: string;
    vehicleApplication: string;
    vehicleSpecification: string;
}

export interface LiveForecastRimFilterOption {
    vehicleSerialNumberList: LiveForecastSelectionFilterOption;
    vehicleSpecifiationList: LiveForecastSelectionFilterOption;
    rimSizeList: LiveForecastSelectionFilterOption;
    rimSpecificationList: LiveForecastSelectionFilterOption;
    filter: LiveForecastRimSelectedFilter;
    validate: LiveForecastGroupValueValidation;
}

export interface LiveForecastRimApplyFilter {
    rimSizeList: Array<string>;
    rimSpecificationList: Array<string>;
    vehicleSerialNumberList: Array<string>;
    vehicleSpecificationList: Array<string>;
}

export interface LiveForecastRimSelectedFilter {
    vehicleSerialNumber: Array<string>;
    vehicleSpecification: Array<string>;
    rimSize: Array<string>;
    rimSpecification: Array<string>;
}

export interface LiveForecastRimDisplayOption {
    isAllRim: boolean;
    isScrapRim: boolean;
    isNdtRim: boolean;
}

export interface LiveForecastRimSelectedFilterItem {
    rimSizeList: Array<string>;
    rimSpecificationList: Array<string>;
    vehicleSerialNumberList: Array<string>;
    vehicleSpecificationList: Array<string>;
    filter: LiveForecastRimSelectedFilter;
}


export interface LiveForecastRimApplyFilter {
    rimSizeList: Array<string>;
    rimSpecificationList: Array<string>;
    vehicleSerialNumberList: Array<string>;
    vehicleSpecificationList: Array<string>;
}

//Condition Monitoring
export enum ConditionMonitoringDisplayType {
    Unknown = 0,
    RecommendedAction = 1,
    ActionStatus = 2,
    LastInspectedDate = 3,
}
export interface ConditionMonitoringWidget {
    siteDashboardWidgetId: string;
    displayBy: ConditionMonitoringDisplayType;
    result?: ConditionMonitoringWidgetDisplayByActionItem;
}

export type ConditionMonitoringWidgetResult = {key: string, value: number}[];

export interface ConditionMonitoringWidgetDisplayByActionItem {
    actionStatus: ConditionMonitoringWidgetResult;
    lastInspectedDate: ConditionMonitoringWidgetResult;
    recommendedAction: ConditionMonitoringWidgetResult;
}

export interface ConditionMonitoringDisplay {
    text: string,
    sequence: number,
}

export interface NitrogenComplianceWidget {
    siteDashboardWidgetId: string;
    nitrogenEnabled: boolean;
    minimumNitrogen: number;
    vehicles: Array<NitrogenComplianceWidgetVehicleResult>;
    hasReceivedResult: boolean;
}

export interface NitrogenComplianceWidgetVehicleResult {
    serialNumber: string;
    specification: string;
    compliant: boolean;
    tyreFitments: Array<NitrogenComplianceFitments>;
}

export interface NitrogenComplianceFitments {
    serialNumber: string;
    nitrogenFilled: boolean;
    nitrogenPurity: number;
}